import { useMemo } from 'react';
import { useCollectionStore } from '../lib/collectionStore';
import { useAuthStore } from '../lib/supabase';

/**
 * Hook to calculate collection percentages for sets
 * Returns a map of setId -> percentage
 */
export function useCollectionPercentage() {
  const { collection } = useCollectionStore();
  const { isAuthenticated } = useAuthStore();

  const collectionPercentages = useMemo(() => {
    if (!isAuthenticated || !collection.length) {
      return new Map<string, number>();
    }

    // Group collection items by set
    const setCollectionMap = new Map<string, Set<string>>();
    
    collection.forEach(item => {
      if (!setCollectionMap.has(item.setId)) {
        setCollectionMap.set(item.setId, new Set());
      }
      // Add unique card IDs to the set (regardless of quantity or finish)
      setCollectionMap.get(item.setId)!.add(item.cardId);
    });

    // Calculate percentages
    const percentages = new Map<string, number>();
    
    setCollectionMap.forEach((uniqueCards, setId) => {
      // We need the total count from the set data
      // For now, we'll store the count of unique cards collected
      // The actual percentage calculation will be done in the sidebar
      // where we have access to the set total
      percentages.set(setId, uniqueCards.size);
    });

    return percentages;
  }, [collection, isAuthenticated]);

  const getCollectionPercentage = (setId: string, setTotal: number): number => {
    if (!isAuthenticated || setTotal === 0) {
      return 0;
    }
    
    const uniqueCardsCollected = collectionPercentages.get(setId) || 0;
    return Math.round((uniqueCardsCollected / setTotal) * 100);
  };

  const getUniqueCardsCollected = (setId: string): number => {
    return collectionPercentages.get(setId) || 0;
  };

  return {
    getCollectionPercentage,
    getUniqueCardsCollected,
    collectionPercentages
  };
} 